import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Webhook, Shield, Code, Copy, ChevronRight, ExternalLink, CheckCircle2,
  AlertTriangle, ArrowLeft, Clock, ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Two-tier docs page.
//   Public tier: overview + "what you get" + flow narrative. Indexable by
//     Google so prospective creators can confirm we support webhook delivery
//     before they apply.
//   Authenticated tier: signature scheme, exact header names, replay window,
//     payload shape, response contract, code samples. Gated behind creator
//     login on 2026-05-21 — earlier the full HMAC/header blueprint was free
//     for competitors to copy. Logged-in creators see the same content as
//     before; pre-signup visitors see a sign-in prompt where the implementation
//     details used to be.

const PYTHON_EXAMPLE = `# Python (FastAPI / Flask) — verify the X-Theailedger-Signature header
import hmac, hashlib, time, os
from fastapi import FastAPI, Request, HTTPException

WEBHOOK_SECRET = os.environ["THEAILEDGER_WEBHOOK_SECRET"]
TOLERANCE_SECONDS = 300  # reject anything older than 5 minutes

app = FastAPI()

@app.post("/theailedger-purchase")
async def handle_purchase(request: Request):
    body = await request.body()
    sig_header = request.headers.get("x-theailedger-signature", "")
    ts_header = request.headers.get("x-theailedger-timestamp", "0")

    # 1. Replay protection
    if abs(time.time() - int(ts_header)) > TOLERANCE_SECONDS:
        raise HTTPException(400, "stale timestamp")

    # 2. Constant-time signature compare
    expected = "v1=" + hmac.new(
        WEBHOOK_SECRET.encode(),
        f"{ts_header}.".encode() + body,
        hashlib.sha256,
    ).hexdigest()
    if not hmac.compare_digest(expected, sig_header):
        raise HTTPException(400, "bad signature")

    # 3. You're verified — provision the customer
    payload = await request.json()
    license_key = await provision_user(
        email=payload["buyer_email"],
        order_id=payload["order_id"],
    )

    # 4. Respond with the key (synchronous mode)
    return {
        "license_key": license_key,
        "instructions": "Use as the X-API-Key header. Docs: https://yourapp.com/docs",
    }
`;

const NODE_EXAMPLE = `// Node.js / Express — verify and respond
const crypto = require('crypto');
const express = require('express');

const SECRET = process.env.THEAILEDGER_WEBHOOK_SECRET;
const app = express();

// IMPORTANT: use raw body parser so the signature matches
app.post(
  '/theailedger-purchase',
  express.raw({ type: 'application/json' }),
  (req, res) => {
    const sig = req.header('X-Theailedger-Signature') || '';
    const ts = req.header('X-Theailedger-Timestamp') || '0';

    // 1. Replay protection (5-min window)
    if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) {
      return res.status(400).send('stale timestamp');
    }

    // 2. Verify signature
    const expected = 'v1=' + crypto
      .createHmac('sha256', SECRET)
      .update(\`\${ts}.\`)
      .update(req.body)
      .digest('hex');
    if (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(sig))) {
      return res.status(400).send('bad signature');
    }

    // 3. Parse + provision
    const { buyer_email, order_id, product_id } = JSON.parse(req.body);
    const licenseKey = provisionUser({ buyer_email, order_id });

    // 4. Respond
    res.json({
      license_key: licenseKey,
      instructions: 'Use as the X-API-Key header.',
    });
  }
);
`;

const GO_EXAMPLE = `// Go — verify and respond
package main

import (
	"crypto/hmac"
	"crypto/sha256"
	"encoding/hex"
	"encoding/json"
	"io"
	"net/http"
	"os"
	"strconv"
	"time"
)

var secret = []byte(os.Getenv("THEAILEDGER_WEBHOOK_SECRET"))

type purchase struct {
	BuyerEmail string  \`json:"buyer_email"\`
	OrderID    string  \`json:"order_id"\`
	ProductID  string  \`json:"product_id"\`
	AmountUSD  float64 \`json:"amount_usd"\`
}

func handle(w http.ResponseWriter, r *http.Request) {
	body, _ := io.ReadAll(r.Body)
	ts, _ := strconv.ParseInt(r.Header.Get("X-Theailedger-Timestamp"), 10, 64)
	if time.Now().Unix()-ts > 300 || ts-time.Now().Unix() > 300 {
		http.Error(w, "stale", 400); return
	}
	mac := hmac.New(sha256.New, secret)
	mac.Write([]byte(r.Header.Get("X-Theailedger-Timestamp") + "."))
	mac.Write(body)
	expected := "v1=" + hex.EncodeToString(mac.Sum(nil))
	if !hmac.Equal([]byte(expected), []byte(r.Header.Get("X-Theailedger-Signature"))) {
		http.Error(w, "bad sig", 400); return
	}
	var p purchase
	json.Unmarshal(body, &p)
	key := provisionUser(p.BuyerEmail, p.OrderID)
	json.NewEncoder(w).Encode(map[string]string{
		"license_key":  key,
		"instructions": "Use as the X-API-Key header.",
	})
}
`;

const PAYLOAD_EXAMPLE = `{
  "event": "purchase.completed",
  "product_id": "prod_abc123",
  "order_id": "order_def456",
  "buyer_email": "buyer@example.com",
  "buyer_name": "Jane Doe",
  "amount_usd": 29.00,
  "timestamp": 1738872000
}`;

const RESPONSE_EXAMPLES = [
  {
    label: 'Synchronous (recommended)',
    body: `{
  "license_key": "ak_live_8X2pqr3sN9vF...",
  "instructions": "Use as your X-API-Key header. 5,000 calls/mo included."
}`,
    note: 'theailedger forwards this key + instructions in the buyer\'s order confirmation email.',
  },
  {
    label: 'Async (deliver yourself)',
    body: `{
  "async": true
}`,
    note: 'Buyer sees "Your access details are being provisioned and will arrive in a separate email from the creator". You handle delivery via your own system.',
  },
];

const HEADERS_TABLE = [
  ['Content-Type', 'application/json'],
  ['User-Agent', 'theailedger-webhook/1.0'],
  ['X-Theailedger-Event', 'purchase.completed'],
  ['X-Theailedger-Timestamp', 'Unix timestamp (seconds) — reject if drift > 5 min'],
  ['X-Theailedger-Signature', 'v1=<hex(hmac_sha256(secret, "{timestamp}.{body}"))>'],
];

export default function DocsWebhooks() {
  const navigate = useNavigate();
  const [activeLang, setActiveLang] = useState('python');

  // Admin-only access. No creator has asked to see the webhook integration
  // recipe since the page launched, but it's a copy-paste blueprint for
  // competitors. Locked to admin on 2026-05-21 — kept in the app for
  // internal reference, removed from public + creator surface area.
  const isAdmin = (() => {
    if (typeof window === 'undefined') return false;
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      return Boolean(
        u && (u.role === 'admin' || u.account_type === 'admin' || u.is_admin === true)
      );
    } catch {
      return false;
    }
  })();

  const codeFor = {
    python: PYTHON_EXAMPLE,
    node: NODE_EXAMPLE,
    go: GO_EXAMPLE,
  }[activeLang];

  const copy = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Copy failed');
    }
  };

  // Non-admin visitors see a minimal stub directing them to support.
  // Replaces the prior in-page section-by-section gating because zero
  // creators have asked for these details organically — a contact-support
  // path is the right primitive for the rare creator who actually needs
  // webhook integration help.
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="docs-webhooks-stub">
        <div className="mx-auto max-w-3xl px-6 py-20 md:py-32">
          <Link
            to="/"
            data-testid="docs-back-home"
            className="mb-8 inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to theailedger
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="rounded-lg bg-[#00F0FF]/15 p-2 text-[#00F0FF]">
              <Webhook className="h-6 w-6" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00F0FF]">
              Webhook integration
            </span>
          </div>
          <h1 className="text-3xl font-bold sm:text-4xl">
            Selling API access or per-customer credentials?
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/60 md:text-lg">
            theailedger supports webhook-based delivery — you provision the
            customer&apos;s key the moment payment clears, we handle billing,
            delivery, and the buyer-facing relationship. Our team will walk
            you through the integration in a quick call once your product
            is approved.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="mailto:support@theailedger.ai?subject=Webhook%20integration%20request"
              className="inline-flex items-center gap-1 rounded-full bg-[#00F0FF] px-6 py-3 font-bold text-black hover:bg-[#00F0FF]/90"
              data-testid="docs-stub-email"
            >
              Email support
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button
              type="button"
              onClick={() => navigate('/submit')}
              data-testid="docs-stub-apply"
              className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3 text-sm font-semibold text-white hover:bg-white/[0.06]"
            >
              Apply as creator
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-gradient-to-b from-[#00F0FF]/5 to-transparent">
        <div className="mx-auto max-w-5xl px-4 py-12 md:px-8 md:py-16">
          <Link
            to="/"
            data-testid="docs-back-home"
            className="mb-6 inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to theailedger
          </Link>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-[#00F0FF]/15 p-2 text-[#00F0FF]">
              <Webhook className="h-6 w-6" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00F0FF]">
              Creator docs
            </span>
          </div>
          <h1
            data-testid="docs-webhooks-heading"
            className="mt-4 text-4xl font-bold sm:text-5xl lg:text-6xl"
          >
            Webhook delivery
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/60 md:text-lg">
            Sell API access, subscriptions, or per-customer credentials by letting
            theailedger ping your backend the moment a buyer pays. You provision
            the key — we handle billing, delivery, and the customer relationship.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-10 md:px-8 md:py-16">
        {/* Quick nav */}
        <nav className="mb-12 grid gap-3 md:grid-cols-3">
          {[
            { id: 'flow', label: 'How it flows', icon: <ListChecks className="h-4 w-4" /> },
            { id: 'payload', label: 'Payload + headers', icon: <Code className="h-4 w-4" /> },
            { id: 'verify', label: 'Verify the signature', icon: <Shield className="h-4 w-4" /> },
          ].map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="group flex items-center gap-3 rounded-xl border border-white/10 bg-neutral-950 p-4 transition-colors hover:border-[#00F0FF]/40"
            >
              <div className="rounded-md bg-white/5 p-2 text-white/70 group-hover:text-[#00F0FF]">
                {s.icon}
              </div>
              <span className="flex-1 text-sm font-medium">{s.label}</span>
              <ChevronRight className="h-4 w-4 text-white/30 group-hover:text-[#00F0FF]" />
            </a>
          ))}
        </nav>

        {/* Flow */}
        <section id="flow" className="mb-16 scroll-mt-20">
          <SectionHeader
            kicker="01"
            title="How it flows"
            subtitle="Four steps. Zero buyer friction."
          />
          <ol className="mt-6 space-y-3">
            {[
              [
                'Buyer completes checkout',
                'Stripe processes the payment, theailedger marks the order completed.',
              ],
              [
                'We POST a signed payload to your URL',
                'JSON body with order details, signed via HMAC-SHA256 in the X-Theailedger-Signature header.',
              ],
              [
                'Your backend provisions the customer',
                'Create the API key, register the user in your database, allocate credits — whatever your product needs.',
              ],
              [
                'Respond with the license key (or {async: true})',
                'theailedger includes the key in the buyer\'s order email automatically. Or take {async: true} if you prefer to email the buyer yourself.',
              ],
            ].map(([title, desc], i) => (
              <li
                key={title}
                className="flex gap-4 rounded-xl border border-white/10 bg-neutral-950 p-4"
              >
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#00F0FF]/15 text-sm font-semibold text-[#00F0FF]">
                  {i + 1}
                </div>
                <div>
                  <div className="font-semibold text-white">{title}</div>
                  <div className="mt-0.5 text-sm text-white/60">{desc}</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Payload + headers — admin-only (non-admins exit at the top
            of the component with a contact-support stub). */}
        <section id="payload" className="mb-16 scroll-mt-20">
          <SectionHeader
            kicker="02"
            title="Payload + headers"
            subtitle="Exactly what we send your endpoint."
          />

          <h3 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wider text-white/50">
            Headers
          </h3>
          <div className="overflow-hidden rounded-xl border border-white/10 bg-neutral-950">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-white/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Header</th>
                  <th className="px-4 py-3 text-left font-medium">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {HEADERS_TABLE.map(([h, v]) => (
                  <tr key={h}>
                    <td className="px-4 py-3 font-mono text-xs text-[#00F0FF]">{h}</td>
                    <td className="px-4 py-3 text-white/70">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="mt-10 mb-3 text-sm font-semibold uppercase tracking-wider text-white/50">
            Body
          </h3>
          <CodeBlock
            code={PAYLOAD_EXAMPLE}
            lang="json"
            onCopy={() => copy(PAYLOAD_EXAMPLE, 'Payload copied')}
            testId="docs-payload-example"
          />

          <h3 className="mt-10 mb-3 text-sm font-semibold uppercase tracking-wider text-white/50">
            Your response
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {RESPONSE_EXAMPLES.map((r) => (
              <div
                key={r.label}
                className="overflow-hidden rounded-xl border border-white/10 bg-neutral-950"
              >
                <div className="border-b border-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#00FF94]">
                  {r.label}
                </div>
                <pre className="overflow-x-auto p-4 text-xs text-white/80">{r.body}</pre>
                <div className="border-t border-white/5 bg-white/[0.02] p-3 text-xs text-white/60">
                  {r.note}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Verification — admin-only. */}
        <section id="verify" className="mb-16 scroll-mt-20">
          <SectionHeader
            kicker="03"
            title="Verify the signature"
            subtitle="Constant-time HMAC compare with a 5-minute replay window."
          />

          <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200/90">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-400" />
            <div>
              <strong className="text-amber-100">Always verify before provisioning.</strong>{' '}
              Without verification an attacker who guessed your URL could mint
              free API keys. Use a constant-time compare (
              <code className="rounded bg-black/30 px-1">hmac.compare_digest</code> in Python,
              <code className="ml-1 rounded bg-black/30 px-1">crypto.timingSafeEqual</code> in Node).
            </div>
          </div>

          {/* Lang switcher */}
          <div className="mt-6 flex gap-1 rounded-lg border border-white/10 bg-neutral-900 p-1">
            {[
              { key: 'python', label: 'Python' },
              { key: 'node', label: 'Node.js' },
              { key: 'go', label: 'Go' },
            ].map((l) => (
              <button
                key={l.key}
                data-testid={`docs-lang-${l.key}`}
                onClick={() => setActiveLang(l.key)}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeLang === l.key
                    ? 'bg-[#00F0FF] text-black'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="mt-3">
            <CodeBlock
              code={codeFor}
              lang={activeLang}
              onCopy={() => copy(codeFor, `${activeLang} example copied`)}
              testId={`docs-code-${activeLang}`}
            />
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <Tip
              icon={<Clock className="h-4 w-4" />}
              title="5-minute replay window"
              body={
                <>
                  Compare <code className="rounded bg-white/10 px-1">timestamp</code> against
                  current time. Reject if drift exceeds 300 seconds. Stops anyone replaying a
                  captured payload after the fact.
                </>
              }
            />
            <Tip
              icon={<CheckCircle2 className="h-4 w-4" />}
              title="Compatible with Stripe libraries"
              body={
                <>
                  Same signature shape as Stripe's webhooks. If you've integrated Stripe
                  webhooks before, the verification code is nearly identical — just swap the
                  header name.
                </>
              }
            />
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-2xl border border-[#00F0FF]/30 bg-gradient-to-br from-[#00F0FF]/5 to-transparent p-6 md:p-8">
          <h3 className="text-2xl font-bold">Ready to wire it up?</h3>
          <p className="mt-2 max-w-2xl text-white/60">
            Open your product on the dashboard, switch its delivery method to{' '}
            <strong className="text-white">Webhook</strong>, paste your endpoint URL,
            and click <strong className="text-white">Send test webhook</strong> — your
            HMAC verification code can run against a real signed payload before you
            take a single live order.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/dashboard">
              <Button
                data-testid="docs-cta-dashboard"
                className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
              >
                Open dashboard
              </Button>
            </Link>
            <a
              href="mailto:support@theailedger.ai?subject=Webhook%20integration%20question"
              className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white"
            >
              Email support
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

const SectionHeader = ({ kicker, title, subtitle }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00F0FF]">
      {kicker}
    </div>
    <h2 className="mt-2 text-3xl font-bold text-white">{title}</h2>
    {subtitle && <p className="mt-2 text-white/60">{subtitle}</p>}
  </div>
);

const CodeBlock = ({ code, lang, onCopy, testId }) => (
  <div className="relative overflow-hidden rounded-xl border border-white/10 bg-neutral-950">
    <div className="flex items-center justify-between border-b border-white/5 px-4 py-2">
      <span className="text-xs font-mono text-white/50">{lang}</span>
      <button
        data-testid={`${testId}-copy`}
        onClick={onCopy}
        className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/70 hover:border-[#00F0FF]/40 hover:text-[#00F0FF]"
      >
        <Copy className="h-3 w-3" />
        Copy
      </button>
    </div>
    <pre
      data-testid={testId}
      className="max-h-[480px] overflow-auto p-4 text-xs leading-relaxed text-white/80"
    >
      {code}
    </pre>
  </div>
);

const Tip = ({ icon, title, body }) => (
  <div className="rounded-xl border border-white/10 bg-neutral-950 p-4">
    <div className="mb-2 inline-flex items-center gap-2 text-[#00F0FF]">
      {icon}
      <strong className="text-sm font-semibold">{title}</strong>
    </div>
    <div className="text-sm text-white/60">{body}</div>
  </div>
);
